import { queryOptions, useQuery } from "@tanstack/react-query";

import makes from "../../assets/mocks/makes.json";
import data from "../../assets/mocks/market-performance.json";
import { getDataFilteredByRange } from "../portfolio/utils";
import { mapPayloadToMarketPerfomance } from "./utils";

import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";
import { AxiosMock } from "@/utils/mock";

import type { QueryConfig } from "@/lib/react-query";
import type { DatesRange } from "@/types/entity";
import type { PerformancePayload } from "@/types/market";
import type { Performance } from "@/types/performance";

type MarketMakePerformanceParams = Partial<DatesRange> & {
  makeId: string;
};

if (import.meta.env.MODE === "test") {
  // Mocking the API response for watchlists
  const mock = AxiosMock.getInstance();
  makes.forEach((make) => {
    mock.onGet(`/carburate/market/makes/${make.id}/performance/`).reply(200, data);
  });
  const uri = new RegExp("/carburate/market/makes/\\w+/performance/");
  mock.onGet(uri).reply(200, data);
}

export const getMarketMakePerformance = async ({ makeId }: MarketMakePerformanceParams) => {
  const response = (await api.get(
    `/carburate/market/makes/${makeId}/performance/`
  )) as PerformancePayload;
  return mapPayloadToMarketPerfomance(response);
};

export const getMarketMakePerformanceQueryOptions = ({ makeId }: MarketMakePerformanceParams) => {
  return queryOptions({
    queryKey: ["market", "makes", makeId, "performance"],
    queryFn: () => getMarketMakePerformance({ makeId }),
  });
};

type UseMarketMakePerformanceOptions = MarketMakePerformanceParams & {
  queryConfig?: QueryConfig<typeof getMarketMakePerformance>;
};

export const useMarketMakePerformance = ({
  from,
  makeId,
  queryConfig,
  to,
}: UseMarketMakePerformanceOptions) => {
  let data: Performance;

  const {
    data: response,
    isSuccess,
    ...props
  } = useQuery({
    enabled: hasAccessToken(),
    ...getMarketMakePerformanceQueryOptions({ makeId }),
    ...queryConfig,
  });

  data = response!;
  if (isSuccess) {
    data = getDataFilteredByRange(data, from && to ? { from, to } : undefined);
  }

  return { data, isSuccess, ...props };
};
